/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  defaultIMobileNumber,
  ErrorMessageValidator,
  IMobileNumber,
  MobileNumberSelect,
  OptionTypeBase,
  SelectChangeFunction,
  SignUpFormValidator,
} from "@advapacs/common";
import useCountryCodePrefixDropdown from "@src/hooks/useCountryCodePrefixDropdown";
import { TFunction } from "i18next";
import { useAppDispatch } from "@src/config/store";
import useCallingCodeLocationOptions from "@src/hooks/useCallingCodeLocationOptions";

interface PersonalInformationFormProps {
  t: TFunction;
  wrapperClass?: string;
  disabled?: boolean;
}

interface IPersonalInformationState {
  name: string;
  practiceName: string;
  mobilePhone: string;
}

const defaultPersonalInformation: Readonly<IPersonalInformationState> = {
  name: "",
  practiceName: "",
  mobilePhone: "",
};

export const PersonalInformationForm = forwardRef(
  (
    { t, wrapperClass = "", disabled = false }: PersonalInformationFormProps,
    ref
  ) => {
    const countryCodePrefixes = useCountryCodePrefixDropdown();
    const [mobilePhoneModel, setMobilePhoneModel] =
      useState<IMobileNumber>(defaultIMobileNumber);

    const [model, setModel] = useState<IPersonalInformationState>(
      defaultPersonalInformation
    );
    const validator = useMemo(() => {
      return SignUpFormValidator(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, model]);
    const dispatch = useAppDispatch();
    const callingCode = useCallingCodeLocationOptions();

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (name === "mobilePhone") {
        setMobilePhoneModel({
          ...mobilePhoneModel,
          mobilePhone: value,
        });
      } else {
        setModel({ ...model, [name]: value });
      }
    };

    const onSelectChange: SelectChangeFunction = (
      newValue: OptionTypeBase,
      actionMeta
    ) => {
      const attribute = actionMeta?.name;
      if (attribute) {
        setMobilePhoneModel({
          ...mobilePhoneModel,
          countryCode: newValue,
          mobilePhone: newValue ? mobilePhoneModel.mobilePhone : undefined, // this line will reset the phone number when select box is clear
        });
      }
    };

    useEffect(() => {
      if (
        mobilePhoneModel === defaultIMobileNumber &&
        countryCodePrefixes?.length > 0 &&
        callingCode
      ) {
        const selectItem = countryCodePrefixes.find(
          (f) => f.value == callingCode
        );
        setMobilePhoneModel({
          ...mobilePhoneModel,
          countryCode: selectItem,
        });
      }
    }, [mobilePhoneModel, countryCodePrefixes, dispatch, callingCode]);

    useImperativeHandle(
      ref,
      () => ({
        validate() {
          return {
            personalInformationValidator: validator,
          };
        },
        getData() {
          return { personalInformationModel: model, mobilePhoneModel };
        },
      }),
      [model, validator, mobilePhoneModel]
    );

    return (
      <div className={`personal-information ${wrapperClass}`}>
        <div className="form-group">
          <label htmlFor="name" className="col-form-label ">
            {t("login:your_name")}
            <span className="text-danger">*</span>
          </label>
          <div className="">
            <input
              disabled={disabled}
              type="text"
              className="form-control"
              placeholder={t("login:name")}
              name="name"
              value={model.name}
              onChange={handleInputChange}
            />
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("login:name"),
                model.name,
                "required|max:100"
              )}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="practiceName" className="col-form-label ">
            {t("login:clinic_practice_name")}
          </label>
          <div className="">
            <input
              type="text"
              disabled={disabled}
              value={model.practiceName}
              onChange={handleInputChange}
              className="form-control"
              placeholder={t("login:clinic_practice_name")}
              name="practiceName"
            />
          </div>
        </div>
        <MobileNumberSelect
          readOnly={disabled}
          mobileNumber={mobilePhoneModel}
          countryCode={countryCodePrefixes}
          dBlock
          handleSelectChange={onSelectChange}
          onChange={handleInputChange}
          validator={validator}
          labelName={t("login:contact_number")}
          mobileNumberInvalidMessage={t("login:contact_number")}
          formLabelStyle={"col-form-label "}
        />
      </div>
    );
  }
);
